import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, MapPin, TrendingUp, Eye, MousePointerClick, Send, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner, EmptyState } from "@/components/shared";
import { toast } from "sonner";

const emptyLine = () => ({ description: "", quantity: 1, unit: "unit", unit_price: 0 });

export default function BusinessWorkspacePage() {
  const { companyId } = useParams();
  const [data, setData] = useState(null);
  const [company, setCompany] = useState(null);
  const [promos, setPromos] = useState([]);
  const [awarded, setAwarded] = useState([]);
  const [quoteJob, setQuoteJob] = useState(null);
  const [lines, setLines] = useState([emptyLine()]);
  const [meta, setMeta] = useState({ discount: 0, vat_mode: "exclusive", vat_rate: 5, payment_terms: "50% on start, 50% on completion", validity_days: 30 });

  const loadJobs = useCallback(() => { api.get(`/company/${companyId}/matching-jobs`).then((r) => setData(r.data)).catch(() => setData({ eligible: false, jobs: [] })); }, [companyId]);
  const loadAwarded = useCallback(() => { api.get(`/company/${companyId}/awarded-jobs`).then((r) => setAwarded(r.data)).catch(() => setAwarded([])); }, [companyId]);
  useEffect(() => {
    loadJobs();
    loadAwarded();
    api.get("/my/companies").then((r) => setCompany(r.data.find((c) => c.id === companyId))).catch(() => {});
    api.get(`/company/${companyId}/promotions`).then((r) => setPromos(r.data)).catch(() => {});
  }, [companyId, loadJobs, loadAwarded]);

  const markFinished = async (jobId) => {
    try { await api.post(`/jobs/${jobId}/mark-finished?company_id=${companyId}`); toast.success("Marked finished — the customer will confirm completion"); loadAwarded(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [logoBusy, setLogoBusy] = useState(false);
  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLogoBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/companies/${companyId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCompany((c) => ({ ...c, logo: data.logo }));
      toast.success("Logo updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLogoBusy(false); }
  };

  const respond = async (jobId) => {
    try { await api.post(`/jobs/${jobId}/respond?company_id=${companyId}`); toast.success("Response sent — a private conversation has opened"); loadJobs(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openQuote = (job) => { setQuoteJob(job); setLines([emptyLine()]); };
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const total = () => {
    const sub = lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0), 0);
    const base = Math.max(0, sub - Number(meta.discount || 0));
    const rate = Number(meta.vat_rate) / 100;
    let vat = 0, tot = base;
    if (meta.vat_mode === "exclusive") { vat = base * rate; tot = base + vat; }
    else if (meta.vat_mode === "inclusive") { vat = base - base / (1 + rate); tot = base; }
    return { sub, vat, tot };
  };

  const submitQuote = async () => {
    if (lines.some((l) => !l.description)) { toast.error("Each line needs a description"); return; }
    try {
      await api.post(`/jobs/${quoteJob.id}/quote`, {
        company_id: companyId,
        line_items: lines.map((l) => ({ ...l, quantity: Number(l.quantity), unit_price: Number(l.unit_price) })),
        discount: Number(meta.discount), vat_mode: meta.vat_mode, vat_rate: Number(meta.vat_rate),
        payment_terms: meta.payment_terms, validity_days: Number(meta.validity_days),
      });
      toast.success("Quote submitted to the customer");
      setQuoteJob(null); loadJobs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const t = total();

  return (
    <div className="propora-container py-10" data-testid="business-workspace-page">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-[#4A5568] hover:text-[#007F83] mb-6"><ArrowLeft size={16} /> Back to dashboard</Link>
      <h1 className="font-display font-extrabold text-4xl text-[#0B1733]">{company?.trading_name || "Business workspace"}</h1>
      <p className="text-[#4A5568] mt-1">Respond to matching jobs, send quotes and track your promotions.</p>

      <div className="mt-5 bg-white rounded-2xl border border-[#E3E8E8] p-4 flex items-center gap-4" data-testid="logo-card">
        <div className="w-16 h-16 rounded-xl border border-[#E3E8E8] bg-white flex items-center justify-center overflow-hidden shrink-0">
          {company?.logo ? <img src={company.logo} alt="logo" className="w-full h-full object-contain p-1" /> : <span className="text-xs text-[#4A5568]">No logo</span>}
        </div>
        <div>
          <p className="font-semibold text-[#0B1733] text-sm">Company logo</p>
          <p className="text-xs text-[#4A5568] mb-2">PNG, JPG, WEBP or SVG, up to 3 MB. Shown on your profile and directory cards.</p>
          <label className="inline-block">
            <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="hidden" onChange={uploadLogo} data-testid="logo-file-input" />
            <span className={`inline-flex items-center gap-1.5 text-sm font-medium rounded-full px-4 py-1.5 cursor-pointer ${logoBusy ? "bg-gray-100 text-gray-400" : "bg-[#E8F5F1] text-[#007F83] hover:bg-[#d4ece5]"}`} data-testid="logo-upload-btn">
              {logoBusy ? "Uploading..." : company?.logo ? "Replace logo" : "Add logo"}
            </span>
          </label>
        </div>
      </div>

      <Tabs defaultValue="jobs" className="mt-8">
        <TabsList>
          <TabsTrigger value="jobs" data-testid="ws-tab-jobs">Matching jobs</TabsTrigger>
          <TabsTrigger value="awarded" data-testid="ws-tab-awarded">Awarded jobs</TabsTrigger>
          <TabsTrigger value="promos" data-testid="ws-tab-promos">Promotions</TabsTrigger>
        </TabsList>

        <TabsContent value="jobs" className="mt-6">
          {!data ? <Spinner /> : !data.eligible ? (
            <EmptyState title="Not yet eligible" subtitle={data.reason || "Your company must be approved with a valid licence to respond to jobs."} />
          ) : data.jobs.length === 0 ? (
            <EmptyState title="No matching jobs right now" subtitle="You'll see jobs matching your services and coverage here." />
          ) : (
            <div className="space-y-4">
              {data.jobs.map((j) => (
                <div key={j.id} className="bg-white rounded-2xl border border-[#E3E8E8] p-5" data-testid={`ws-job-${j.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="font-display font-bold text-lg text-[#0B1733]">{j.title}</h3>
                      <p className="text-sm text-[#4A5568] inline-flex items-center gap-2 mt-1">{j.service_name} · <MapPin size={13} /> {j.area} · {j.urgency}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-[#4A5568]">{j.remaining_slots} of {j.max_responses} slots left</p>
                      <div className="mt-2 flex gap-2">
                        {!j.responded ? (
                          <Button size="sm" onClick={() => respond(j.id)} data-testid={`respond-${j.id}`} disabled={j.remaining_slots === 0} className="rounded-full bg-[#0B1733] hover:bg-[#0B1733]/90 text-white">{j.remaining_slots === 0 ? "Full" : "Respond"}</Button>
                        ) : (
                          <Button size="sm" onClick={() => openQuote(j)} data-testid={`quote-btn-${j.id}`} className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white">{j.has_quote ? "Update quote" : "Send quote"}</Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="awarded" className="mt-6">
          {awarded.length === 0 ? (
            <EmptyState title="No awarded jobs yet" subtitle="When a customer awards you a job, it appears here so you can mark the work finished." />
          ) : (
            <div className="space-y-4">
              {awarded.map((j) => (
                <div key={j.id} className="bg-white rounded-2xl border border-[#E3E8E8] p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`awarded-job-${j.id}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-display font-bold text-lg text-[#0B1733]">{j.title}</h3>
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#E8F5F1] text-[#007F83] capitalize">{j.status.replace(/_/g, " ")}</span>
                    </div>
                    <p className="text-sm text-[#4A5568] mt-1 inline-flex items-center gap-1"><MapPin size={13} /> {j.area} · {j.service_name}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Link to="/dashboard/messages" className="text-sm text-[#007F83] hover:underline">Messages</Link>
                    {(j.status === "awarded" || j.status === "in_progress") && (
                      <Button size="sm" onClick={() => markFinished(j.id)} data-testid={`mark-finished-${j.id}`} className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white">Mark work finished</Button>
                    )}
                    {j.status === "completion_pending" && <span className="text-sm text-amber-700">Awaiting customer confirmation</span>}
                    {j.status === "completed" && <span className="text-sm text-green-700 inline-flex items-center gap-1"><Check size={14} /> Completed{j.has_review ? " · reviewed" : ""}</span>}
                    {j.status === "disputed" && <span className="text-sm text-red-600">Under admin review</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="promos" className="mt-6">
          {promos.length === 0 ? (
            <EmptyState title="No promotions yet" subtitle="Ask the platform to feature your company. During the launch pilot, featured placements can be granted free by an admin.">
              <Button onClick={() => toast("Promotion request noted — an admin will review eligibility. (Self-service checkout activates when a payment provider is enabled.)")} data-testid="request-promo-btn" className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white"><Send size={15} className="mr-1.5" /> Request promotion</Button>
            </EmptyState>
          ) : (
            <div className="space-y-4">
              {promos.map((p) => (
                <div key={p.id} className="bg-white rounded-2xl border border-[#E3E8E8] p-5" data-testid={`ws-promo-${p.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-display font-bold text-[#0B1733] capitalize">{p.placement.replace(/_/g, " ")}</h3>
                      <p className="text-sm text-[#4A5568]">{p.start_date} → {p.end_date}</p>
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${p.status === "active" ? "bg-[#E8F5F1] text-[#007F83]" : "bg-gray-100 text-gray-600"}`}>{p.status}</span>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-3">
                    <div className="bg-[#FAFAF7] rounded-xl p-3 text-center"><Eye size={16} className="mx-auto text-[#007F83]" /><p className="font-display font-bold text-xl text-[#0B1733] mt-1">{p.metrics.impressions}</p><p className="text-[11px] text-[#4A5568]">Impressions</p></div>
                    <div className="bg-[#FAFAF7] rounded-xl p-3 text-center"><MousePointerClick size={16} className="mx-auto text-[#007F83]" /><p className="font-display font-bold text-xl text-[#0B1733] mt-1">{p.metrics.profile_clicks}</p><p className="text-[11px] text-[#4A5568]">Profile clicks</p></div>
                    <div className="bg-[#FAFAF7] rounded-xl p-3 text-center"><TrendingUp size={16} className="mx-auto text-[#007F83]" /><p className="font-display font-bold text-xl text-[#0B1733] mt-1">{p.metrics.request_conversions}</p><p className="text-[11px] text-[#4A5568]">Quote requests</p></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!quoteJob} onOpenChange={(o) => !o && setQuoteJob(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
          <DialogHeader><DialogTitle className="font-display">Quote for: {quoteJob?.title}</DialogTitle>
          <DialogDescription>Add line items, set VAT treatment and payment terms. Totals are calculated for you.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <Input className="col-span-5" placeholder="Description" value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`line-desc-${i}`} />
                <Input className="col-span-2" type="number" placeholder="Qty" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} data-testid={`line-qty-${i}`} />
                <Input className="col-span-2" placeholder="Unit" value={l.unit} onChange={(e) => setLine(i, "unit", e.target.value)} />
                <Input className="col-span-2" type="number" placeholder="Price" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} data-testid={`line-price-${i}`} />
                <button className="col-span-1 text-red-500" onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))} aria-label="remove"><Trash2 size={16} /></button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setLines((ls) => [...ls, emptyLine()])} data-testid="add-line-btn" className="rounded-full"><Plus size={14} className="mr-1" /> Add line</Button>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <div><Label>Discount (AED)</Label><Input type="number" value={meta.discount} onChange={(e) => setMeta({ ...meta, discount: e.target.value })} data-testid="quote-discount" className="mt-1.5" /></div>
              <div><Label>VAT treatment</Label>
                <Select value={meta.vat_mode} onValueChange={(v) => setMeta({ ...meta, vat_mode: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="quote-vat-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="exclusive">VAT exclusive (added)</SelectItem>
                    <SelectItem value="inclusive">VAT inclusive</SelectItem>
                    <SelectItem value="not_applicable">Not applicable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {meta.vat_mode !== "not_applicable" && <div><Label>VAT rate %</Label><Input type="number" value={meta.vat_rate} onChange={(e) => setMeta({ ...meta, vat_rate: e.target.value })} data-testid="quote-vat-rate" className="mt-1.5" /></div>}
              <div><Label>Validity (days)</Label><Input type="number" value={meta.validity_days} onChange={(e) => setMeta({ ...meta, validity_days: e.target.value })} className="mt-1.5" /></div>
              <div className="col-span-2"><Label>Payment terms</Label><Textarea rows={2} value={meta.payment_terms} onChange={(e) => setMeta({ ...meta, payment_terms: e.target.value })} className="mt-1.5" /></div>
            </div>

            <div className="bg-[#E8F5F1] rounded-xl p-4 text-sm" data-testid="quote-preview-totals">
              <div className="flex justify-between text-[#4A5568]"><span>Subtotal</span><span>AED {t.sub.toFixed(2)}</span></div>
              <div className="flex justify-between text-[#4A5568]"><span>{meta.vat_mode === "not_applicable" ? "VAT (n/a)" : `VAT ${meta.vat_rate}%`}</span><span>AED {t.vat.toFixed(2)}</span></div>
              <div className="flex justify-between font-display font-extrabold text-[#0B1733] text-base mt-1"><span>Total</span><span>AED {t.tot.toFixed(2)}</span></div>
            </div>
            <Button onClick={submitQuote} data-testid="submit-quote-btn" className="w-full rounded-full bg-[#007F83] hover:bg-[#006B70] text-white">Submit quote</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
